// =====================================================================
// manualMediaScan — browser-side scan for Excerptus-hosted <img> tags
// inside a published body. Mirrors the regex + bucket allowlist used by
// `supabase/functions/_shared/publishing/mediaHandoff.ts` so the manual
// chip "mark sent" path can seed `content_publication_pending_media`
// rows without an edge round-trip.
// =====================================================================

export const MANUAL_MEDIA_BUCKETS = [
  "composer-block-images",
  "brief-images",
] as const;

export type ManualMediaBucket = (typeof MANUAL_MEDIA_BUCKETS)[number];

export type ManualMediaHit = {
  bucket: ManualMediaBucket;
  path: string;
  filename: string;
  alt: string | null;
  original_url: string;
};

const IMG_TAG_RX = /<img\b[^>]*>/gi;
const SRC_ATTR_RX = /\bsrc\s*=\s*(?:"([^"]+)"|'([^']+)'|([^\s>]+))/i;
const ALT_ATTR_RX = /\balt\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;

const parseStorageUrl = (
  url: string,
): { bucket: ManualMediaBucket; path: string } | null => {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  if (!/(^|\.)supabase\.(co|in)$/i.test(u.hostname)) return null;
  const m = u.pathname.match(
    /^\/storage\/v1\/object\/(?:public|sign|authenticated)\/([^/]+)\/(.+)$/,
  );
  if (!m) return null;
  const bucket = m[1] as ManualMediaBucket;
  if (!(MANUAL_MEDIA_BUCKETS as readonly string[]).includes(bucket)) return null;
  let path = m[2];
  try {
    path = decodeURIComponent(path);
  } catch {
    // leave raw — refresh-signed-url will surface the storage error if it matters.
  }
  return { bucket, path };
};

const filenameFromPath = (path: string): string => {
  const last = path.split("/").pop() ?? path;
  return last || "image";
};

const extractAlt = (tag: string): string | null => {
  const m = tag.match(ALT_ATTR_RX);
  if (!m) return null;
  return (m[1] ?? m[2] ?? m[3] ?? null) || null;
};

/** Walk `html` for `<img>` tags whose `src` points at one of the Excerptus
 *  handoff buckets. Coalesces duplicates by storage URL so each unique image
 *  produces exactly one hit. Returns `[]` for empty/non-matching input. */
export const extractExcerptusImagesFromHtml = (
  html: string | null | undefined,
): ManualMediaHit[] => {
  if (!html) return [];
  const seen = new Set<string>();
  const out: ManualMediaHit[] = [];
  for (const tag of html.match(IMG_TAG_RX) ?? []) {
    const srcMatch = tag.match(SRC_ATTR_RX);
    const raw = srcMatch ? (srcMatch[1] || srcMatch[2] || srcMatch[3] || "").trim() : "";
    if (!raw || seen.has(raw)) continue;
    const parsed = parseStorageUrl(raw);
    if (!parsed) continue;
    seen.add(raw);
    out.push({
      bucket: parsed.bucket,
      path: parsed.path,
      filename: filenameFromPath(parsed.path),
      alt: extractAlt(tag),
      original_url: raw,
    });
  }
  return out;
};
